var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RenderIf, VerticalCardLoader } from 'syf-component-library/ui/atoms';
import { Inset, Stack } from 'syf-component-library/ui/spacing';
import { useIVASetChatStatus } from 'iva-react-web-ui';
import { triggerAnalyticsEvent } from 'syf-js-utilities/helpers';
import { AnalyticsValues, TriggerAnalyticsKeys, backToAccountManagementDataProps, onlineChatLinkDataProps, requestRefundDataProps } from '../../../../const/analytics';
import NetworkErrorIllustration from '../../../illustrations/network-error';
import { errorCodes, errorCodesNumber } from '../../../../const/errorCodes';
import { StyledFlex } from '../../../../styles/common';
import { StyledTitle } from '../../../../styles/text';
import CBRContext from '../../context';
import queryParamsToObject from '../../credit-balance-refund-card/queryParamsToObject';
import store from '../../../../helpers/store';
import DisputeTransactionIcon from '../../../icon/DisputeTransaction';
import CardOption from './CardOption';
import { BalanceOptionSection, BalanceValue } from './subcomponents';
import GenericError from '../../error/generic-error';
var ReviewBalanceOptions = function (_a) {
    var _b;
    var currentBalance = _a.currentBalance, eligibilityData = _a.eligibilityData, isEligibilityFetching = _a.isEligibilityFetching, onRefundRequestExecution = _a.onRefundRequestExecution;
    var t = useTranslation().t;
    var _c = useState(''), otpErrorCode = _c[0], setOtpErrorCode = _c[1];
    var pointOfNeedChat = useIVASetChatStatus().pointOfNeedChat;
    var _d = useContext(CBRContext), accountId = _d.accountId, backToAccountManagerHandler = _d.backToAccountManagerHandler;
    useEffect(function () {
        var _a;
        if (accountId) {
            triggerAnalyticsEvent((_a = {},
                _a[TriggerAnalyticsKeys.pageKind] = AnalyticsValues.cbr,
                _a[TriggerAnalyticsKeys.pageFunction] = AnalyticsValues.manageCreditBalance,
                _a[TriggerAnalyticsKeys.pageName] = AnalyticsValues.view,
                _a));
        }
    }, [accountId]);
    useEffect(function () {
        var otpErrorCallbackParams = store.get('otpErrorCallbackParams', 'session');
        if (otpErrorCallbackParams) {
            var errorObjectParams = queryParamsToObject(otpErrorCallbackParams);
            var errorCode = otpErrorCallbackParams;
            if ('error' in errorObjectParams) {
                errorCode = errorObjectParams.error;
            }
            if (errorCode !== errorCodes.accessDenied) {
                store.remove('otpErrorCallbackParams', 'session');
                store.remove('cbr-mfe_isCbrExecution', 'session');
            }
            setOtpErrorCode(errorCode);
        }
    }, []);
    var isRefundEligible = eligibilityData === null || eligibilityData === void 0 ? void 0 : eligibilityData.refundEligible;
    var isAccessDenied = otpErrorCode === errorCodes.accessDenied;
    var statusCode = eligibilityData === null || eligibilityData === void 0 ? void 0 : eligibilityData.statusCode;
    var isBusinessErrorStatusCode = statusCode === 422;
    var businessErrorCode = (_b = eligibilityData === null || eligibilityData === void 0 ? void 0 : eligibilityData.businessErrors) === null || _b === void 0 ? void 0 : _b.some(function (err) { return err.code === errorCodesNumber.refundInProgress; });
    var isBusinessError = isBusinessErrorStatusCode && !businessErrorCode;
    var isOtherBusinessError = Boolean(statusCode && !isBusinessErrorStatusCode);
    useEffect(function () {
        var _a;
        if (isOtherBusinessError) {
            triggerAnalyticsEvent((_a = {},
                _a[TriggerAnalyticsKeys.pageKind] = AnalyticsValues.cbr,
                _a[TriggerAnalyticsKeys.pageFunction] = AnalyticsValues.manageCreditBalance,
                _a[TriggerAnalyticsKeys.message] = AnalyticsValues.errorMessage,
                _a[TriggerAnalyticsKeys.pageName] = AnalyticsValues.error,
                _a));
        }
    }, [isOtherBusinessError]);
    return (_jsx("div", { children: isEligibilityFetching ? (_jsx(Inset, { children: _jsx(VerticalCardLoader, {}) })) : (_jsxs(_Fragment, { children: [_jsx(RenderIf, __assign({ condition: isOtherBusinessError }, { children: _jsx(GenericError, { image: _jsx(NetworkErrorIllustration, {}), titleKey: "error.networkHeading", subtitleKey: "error.networkMessage" }) })), _jsx(RenderIf, __assign({ condition: !isOtherBusinessError }, { children: _jsx(StyledFlex, __assign({ justifyContent: "center" }, { children: _jsx(Inset, __assign({ all: "xlarge" }, { children: _jsxs(BalanceOptionSection, __assign({ direction: "column", justifyContent: "center", alignItems: "center" }, { children: [_jsx(DisputeTransactionIcon, {}), _jsx(Stack, { size: "large" }), _jsx(Inset, __assign({ vertical: "medium" }, { children: _jsx(StyledTitle, __assign({ size: "medium", align: "center" }, { children: t('header.reviewCurrentBalance') })) })), _jsx(Stack, {}), _jsxs(BalanceValue, { children: ["-$", currentBalance.slice(1)] }), _jsx(Stack, { size: "large" }), _jsx(CardOption, { header: t('header.applyNextPurchase'), body: t('subtext.applyCurrentBalanceAutomatically'), text: t('button.backToAccountManager'), isButton: true, onClick: function () { return backToAccountManagerHandler(); }, analyticProps: backToAccountManagementDataProps }), _jsx(Stack, {}), _jsx(RenderIf, __assign({ condition: Boolean(isRefundEligible) && !isAccessDenied }, { children: _jsx(CardOption, { header: t('header.requestRefundCheck'), body: t('subtext.receiveRefundCheck'), text: t('button.requestRefundCheck'), isButton: true, onClick: onRefundRequestExecution, analyticProps: requestRefundDataProps }) })), _jsx(RenderIf, __assign({ condition: isBusinessError || isAccessDenied }, { children: _jsx(CardOption, { header: t('header.requestRefundCheck'), body: "".concat(t('subtext.receiveRefundCheckAccessDeniedText'), " "), text: "".concat(t('subtext.onlineChatButton'), "."), onClick: function () {
                                                pointOfNeedChat('CreditBalanceRefund', accountId);
                                            }, analyticProps: onlineChatLinkDataProps }) }))] })) })) })) }))] })) }));
};
export default ReviewBalanceOptions;
